import { APIReferenceType, ArrowType, LineType } from '@ardoq/api-types';
import { GraphItem, GraphItemMetadata } from './GraphItem';
import { filterInterface } from '@ardoq/filter-interface';
import SparkMD5 from 'spark-md5';
import { DiffType, ReferenceLabelSource } from '@ardoq/data-model';
import { componentInterface } from '@ardoq/component-interface';
import { componentOperations } from '@ardoq/core';
import { IS_PARENT_OF } from '@ardoq/global-consts';
import { PARENT_CHILD_LINE_TYPE } from './consts';
import { ItemLabels } from '../types';
import { GraphEdgeBase } from './GraphEdgeBase';
import { referenceInterface } from '@ardoq/reference-interface';
import { getParentChildEdgeDiffType } from './utils';
import { ComponentGraphParentChildEdgeModel } from './types';
import { type I18nLocale } from '@ardoq/i18n';
import { isParentOfLabel } from '../labels/parentChildLabels';

const lineColor = '#000';
const lineBeginning = ArrowType.NONE;
const lineEnding = ArrowType.DIAMOND;

/*
  This type of reference is used to represent components hierarchies.
  There is no according equivalent in the models. They are created on the fly
  to be able to traverse a graph in a consistent way.
*/
export class ParentChildGraphEdge
  extends GraphEdgeBase
  implements ComponentGraphParentChildEdgeModel
{
  static create(
    modelId: string,
    // The parent id
    sourceId: string,
    // The child id
    targetId: string,
    graphNodeMap: Map<string, GraphItem>,
    id?: string,
    itemLabels?: ItemLabels
  ) {
    // TODO do we need new ids for the graph items?
    const sourceGraphNode = graphNodeMap.get(sourceId);
    const targetGraphNode = graphNodeMap.get(targetId);
    if (!sourceGraphNode || !targetGraphNode) {
      throw new Error(
        'Trying to create an edge without according source and/or target nodes'
      );
    }
    return new ParentChildGraphEdge(
      modelId,
      sourceGraphNode.id,
      targetGraphNode.id,
      sourceGraphNode.modelId,
      targetGraphNode.modelId,
      id,
      itemLabels
    );
  }

  static getType(i18nLocale?: I18nLocale): APIReferenceType {
    return {
      name: i18nLocale ? isParentOfLabel(i18nLocale) : IS_PARENT_OF,
      lineBeginning,
      lineEnding,
      color: lineColor,
      line: PARENT_CHILD_LINE_TYPE,
      returnsValue: false,
      id: -1,
      svgStyle: null,
    };
  }

  readonly source: string;
  readonly target: string;
  readonly sourceModelId: string;
  readonly targetModelId: string;
  protected itemLabels: ItemLabels | undefined;

  private constructor(
    modelId: string,
    sourceGraphNodeId: string,
    targetGraphNodeId: string,
    sourceModelId: string,
    targetModelId: string,
    id?: string,
    itemLabels?: ItemLabels
  ) {
    const graphItemMetadata: GraphItemMetadata = {
      isComponent: false,
      isReference: referenceInterface.isReference(modelId),
      isGroup: false,
    };
    super(modelId, graphItemMetadata, id);
    this.source = sourceGraphNodeId;
    this.target = targetGraphNodeId;
    this.sourceModelId = sourceModelId;
    this.targetModelId = targetModelId;
    this.itemLabels = itemLabels;
    this.hash = SparkMD5.hash(this.getRawHash());
  }

  override getLabel() {
    const { value } = filterInterface.getReferenceLabelFilter();
    if (
      value === ReferenceLabelSource.REFERENCE_TYPE ||
      value === ReferenceLabelSource.DISPLAY_TEXT_OR_REFERENCE_TYPE
    ) {
      return IS_PARENT_OF;
    }
    return '';
  }

  override hasURLFields() {
    return false;
  }

  override getCSS() {
    return 'parent-child-reference';
  }
  override getLineBeginning() {
    return lineBeginning;
  }
  override getLineEnding() {
    return lineEnding;
  }
  override getLine(): LineType {
    return PARENT_CHILD_LINE_TYPE;
  }
  override getItemLabels(): ItemLabels | undefined {
    return this.itemLabels;
  }
  override setItemLabels(itemLabels: ItemLabels): void {
    this.itemLabels = itemLabels;
  }
  override getVisualDiffType() {
    const [sourceDiffType, targetDiffType] = [
      this.sourceModelId,
      this.targetModelId,
    ].map(modelId => {
      const component = componentInterface.get(modelId);
      return component
        ? componentOperations.getVisualDiffType(component)
        : DiffType.NONE;
    });

    return getParentChildEdgeDiffType(
      sourceDiffType,
      targetDiffType,
      this.hasScenarioParentChanged()
    );
  }

  hasScenarioParentChanged(): boolean {
    // componentInterface.hasParentChanged does not access BackBone collection.
    // It is placed there because it accesses a global state.
    return componentInterface.hasParentChanged(this.targetModelId);
  }

  protected getRawHash() {
    return `${super.getRawHash()}${this.getLabel()}${this.sourceModelId}${
      this.targetModelId
    }`;
  }
}
